/**
 * ResponsiveGrid Component
 * Auto-responsive grid layout with customizable columns
 *
 * Features:
 * - Automatic column adjustment based on screen size
 * - Customizable gap spacing
 * - Touch-friendly card layouts
 */

import { ReactNode } from 'react';
import { cn } from '../../utils/responsive';

export interface ResponsiveGridProps {
  children: ReactNode;
  className?: string;
  /** Number of columns on mobile (default: 1) */
  mobileCols?: 1 | 2;
  /** Number of columns on tablet (default: 2) */
  tabletCols?: 2 | 3;
  /** Number of columns on desktop (default: 4) */
  desktopCols?: 2 | 3 | 4 | 5 | 6;
  /** Gap between items - mobile (default: 3) */
  mobileGap?: number;
  /** Gap between items - desktop (default: 4) */
  desktopGap?: number;
  /** Whether to use equal height cards */
  equalHeight?: boolean;
}

export function ResponsiveGrid({
  children,
  className,
  mobileCols = 1,
  tabletCols = 2,
  desktopCols = 4,
  mobileGap = 3,
  desktopGap = 4,
  equalHeight = false,
}: ResponsiveGridProps) {
  // Build responsive grid classes
  const gridClasses = cn(
    'grid',
    // Mobile columns
    mobileCols === 1 ? 'grid-cols-1' : 'grid-cols-2',
    // Tablet columns
    tabletCols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3',
    // Desktop columns
    `lg:grid-cols-${desktopCols}`,
    // Gap
    `gap-${mobileGap} lg:gap-${desktopGap}`,
    // Equal height
    equalHeight && 'auto-rows-fr',
    className
  );

  return <div className={gridClasses}>{children}</div>;
}

/**
 * ResponsiveGridItem Component
 * Individual grid item with consistent styling
 */
interface ResponsiveGridItemProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
}

export function ResponsiveGridItem({
  children,
  className,
  onClick,
  selected = false,
  disabled = false,
}: ResponsiveGridItemProps) {
  return (
    <div
      className={cn(
        'bg-white dark:bg-gray-800 rounded-lg border border-stone-200 dark:border-gray-700 p-4 lg:p-6',
        'transition-all duration-200',
        !disabled && 'hover:border-yellow-400 hover:shadow-accent',
        onClick && !disabled && 'cursor-pointer active:scale-[0.99]',
        selected && 'border-yellow-400 bg-yellow-50/30 dark:bg-yellow-900/20 shadow-accent',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={!disabled ? onClick : undefined}
    >
      {children}
    </div>
  );
}

/**
 * ResponsiveCardGrid - Preset for product/content cards
 */
interface ResponsiveCardGridProps {
  children: ReactNode;
  className?: string;
}

export function ResponsiveCardGrid({ children, className }: ResponsiveCardGridProps) {
  return (
    <ResponsiveGrid
      mobileCols={1}
      tabletCols={2}
      desktopCols={3}
      mobileGap={3}
      desktopGap={4}
      equalHeight
      className={className}
    >
      {children}
    </ResponsiveGrid>
  );
}

/**
 * ResponsiveStatsGrid - Preset for dashboard stats cards
 */
interface ResponsiveStatsGridProps {
  children: ReactNode;
  className?: string;
}

export function ResponsiveStatsGrid({ children, className }: ResponsiveStatsGridProps) {
  return (
    <ResponsiveGrid
      mobileCols={2}
      tabletCols={2}
      desktopCols={4}
      mobileGap={2}
      desktopGap={4}
      equalHeight
      className={className}
    >
      {children}
    </ResponsiveGrid>
  );
}
